import type { BasicColumn, FormProps } from '@mts/components/Table';
import type { Recordable } from '@mts-fe/types';
import type { TableProps } from 'ant-design-vue';
import { isArray } from '.';

/** 表单：通用配置 */
export const getFormConfig = <T extends FormProps>(config: T): T => {
  const { layout = 'vertical' } = config;
  return {
    labelWrap: true,
    labelAlign: 'right',
    autoSetPlaceHolder: true,
    ...config,
    layout,
    rowProps: { gutter: 12, ...config.rowProps },
    labelWidth: { vertical: '100%', horizontal: config.labelWidth ?? 100 }[layout],
  };
};

/** 表格列宽 */
export const COL_WIDTH = {
  /** 两字宽（如：排序） */
  S: 60,
  /** 四字宽（如：状态、操作人） */
  M: 88,
  /** 六字宽（如：单号） */
  L: 130,
  /** 超宽 */
  XL: 180,
  /** 操作时间 */
  TIME: 160,
} as const;

export const BASE_COLUMN: Recordable<BasicColumn> = {
  createByNickname: { title: 'g.25090021', dataIndex: 'createByNickname', defaultHidden: true },
  updateByNickname: { title: 'g.25090022', dataIndex: 'updateByNickname' },
  createTime: {
    title: 'g.25090023',
    dataIndex: 'createTime',
    align: 'center',
    width: COL_WIDTH.TIME,
    defaultHidden: true,
  },
  updateTime: {
    title: 'g.25090024',
    dataIndex: 'updateTime',
    align: 'center',
    width: COL_WIDTH.TIME,
  },
};
export const LOG_COLUMNS = [
  BASE_COLUMN.createByNickname,
  BASE_COLUMN.createTime,
  BASE_COLUMN.updateByNickname,
  BASE_COLUMN.updateTime,
];

/** 表格列：通用配置 */
export const getTableColumns = <T extends BasicColumn>(
  columns: T[],
  columnDefault?: Partial<T>,
): T[] =>
  columns.map((i) => ({
    width: COL_WIDTH.M,
    resizable: true,
    ellipsis: false,
    ...columnDefault,
    ...i,
  }));

/** 表格列排序：根据charCode */
export const charCodeSorter = (sortKey: string) => (a: Recordable, b: Recordable) => {
  if (a.flag === 'summary' || b.flag === 'summary') {
    return 0;
  }

  const aVal = a[sortKey];
  const bVal = b[sortKey];

  if (typeof aVal === 'number' && typeof bVal === 'number') {
    return aVal - bVal;
  }

  const prev = String(aVal);
  const next = String(bVal);

  if (prev === '' && next === '') return 0;
  if (prev === '') return -1;
  if (next === '') return 1;

  // Compare character by character
  const minLen = Math.min(prev.length, next.length);
  for (let i = 0; i < minLen; i++) {
    const charDiff = prev.charCodeAt(i) - next.charCodeAt(i);
    if (charDiff !== 0) return charDiff;
  }

  return prev.length - next.length;
};

/** 表格单元格空值展示 */
export const tableCellTextTransformer =
  (emptyText = ''): TableProps['transformCellText'] =>
  ({ text, column }) =>
    text || text?.length || !column || column.customRender ? text : emptyText;

/** 相同key的字段单元行合并 */
export const getRowSpanCount = (data: any, key: string | string[], index = 0) => {
  if (!isArray(data)) return 1;

  const getValue = (record: Recordable = {}) =>
    isArray(key) ? key.map((k) => record[k]).join('&&') : record[key];

  const curValue = getValue(data[index]);
  const countBeforeSame = (dataList: any[]) => {
    let count = 0;
    for (const item of dataList) {
      if (getValue(item) === curValue) {
        count++;
      } else {
        break;
      }
    }
    return count;
  };

  const prevCount = countBeforeSame(index ? data.slice(0, index).reverse() : []);
  const nextCount = countBeforeSame(index === data.length ? [] : data.slice(index + 1));
  return prevCount ? 0 : prevCount + 1 + nextCount;
};
